<template>
  <div class="rs-page">
    <div class="rs-page-header">
      <h1 class="rs-page-title">Ведомость ресурсов</h1>
    </div>

    <div class="rs-filters-card">
      <div class="rs-filters-card__body">
        <div class="rs-filters-toolbar">
          <div class="rs-filters-main">
            <CustomSelect
              class="rs-filter-field"
              :options="residents"
              label="ЖК"
              option-value-key="resident_id"
              option-label-key="resident_name"
              :value="filters.resident_id"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filters.resident_id"
              @change="onResident"
            />
            <CustomSelect
              class="rs-filter-field"
              :options="projectTypes"
              label="Тип проекта"
              option-value-key="project_type_id"
              option-label-key="project_type_name"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filters.project_type_id"
              :value="filters.project_type_id"
              :disabled="!filters.resident_id"
              @change="onProjectType"
            />
            <CustomSelect
              class="rs-filter-field"
              :options="entrances"
              label="Подъезд"
              option-value-key="entrance"
              option-label-key="entrance_name"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filters.entrance"
              :value="filters.entrance"
              :disabled="!filters.resident_id"
              @change="onEntrance"
            />
            <CustomSelect
              class="rs-filter-field"
              :options="floors"
              label="Этаж"
              option-value-key="level_num"
              option-label-key="floor_name"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filters.level_num"
              :value="filters.level_num"
              :disabled="!filters.resident_id"
              @change="onFloor"
            />
            <CustomSelect
              class="rs-filter-field"
              :options="purchaseTypes"
              label="Вид закупа"
              option-value-key="purchase_type_id"
              option-label-key="purchase_name"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filters.purchase_type_id"
              :value="filters.purchase_type_id"
              :disabled="!filters.resident_id"
              @change="onPurchaseType"
            />
          </div>

          <div class="rs-filters-actions">
            <q-btn
              class="rs-filter-action-btn"
              outline
              color="grey-7"
              no-caps
              dense
              icon="filter_alt_off"
              label="Сбросить"
              :disable="!hasActiveFilters"
              @click="resetFilters"
            />
            <q-btn
              class="rs-filter-action-btn"
              color="positive"
              unelevated
              no-caps
              dense
              icon="download"
              label="Excel"
              :disable="!filters.resident_id || exporting"
              :loading="exporting"
              @click="exportExcel"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="rows.length" class="rs-totals-bar q-mt-sm">
      <div class="rs-total-block">
        <div class="rs-total-label">Итого, тг.</div>
        <div class="rs-total-value">
          <Amount :value="totalSum" round="2" />
        </div>
      </div>
    </div>

    <q-banner v-if="errorText" rounded class="rs-banner q-mt-md">
      <div class="rs-banner__text">{{ errorText }}</div>
    </q-banner>

    <q-linear-progress
      v-if="loading"
      indeterminate
      color="primary"
      track-color="grey-3"
      class="rs-progress q-mt-md"
      rounded
      size="4px"
    />

    <q-card v-if="rows.length" flat bordered class="rs-card rs-card--table q-mt-md">
      <div class="rs-table-scroll">
        <table class="rs-table" cellspacing="0">
          <colgroup>
            <col class="rs-col-code" />
            <col class="rs-col-name" />
            <col class="rs-col-unit" />
            <col class="rs-col-num" />
            <col class="rs-col-num" />
            <col class="rs-col-num" />
            <col class="rs-col-num" />
            <col class="rs-col-purchase" />
          </colgroup>
          <thead class="rs-thead">
            <tr class="rs-tr rs-tr--head">
              <th class="rs-th rs-th--sticky-left">Код ресурса</th>
              <th class="rs-th rs-th--name">Наименование</th>
              <th class="rs-th">Ед. изм.</th>
              <th class="rs-th rs-th--num">Кол-во</th>
              <th class="rs-th rs-th--num">Сметная цена, тг.</th>
              <th class="rs-th rs-th--num">Актуальная цена, тг.</th>
              <th class="rs-th rs-th--num">Сумма, тг.</th>
              <th class="rs-th">Вид закупа</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in rows"
              :key="idx"
              class="rs-tr rs-tr--data"
            >
              <td class="rs-td rs-td--sticky-left rs-td--code">{{ row.kod_resursa || '—' }}</td>
              <td class="rs-td rs-td--name">{{ row.material_name || '—' }}</td>
              <td class="rs-td">{{ row.unit_name || '—' }}</td>
              <td class="rs-td rs-td--num">
                <Amount v-if="row.material_amount != null" :value="row.material_amount" />
                <span v-else class="rs-dash">—</span>
              </td>
              <td class="rs-td rs-td--num">
                <Amount v-if="row.material_price != null" :value="row.material_price" round="2" />
                <span v-else class="rs-dash">—</span>
              </td>
              <td class="rs-td rs-td--num">
                <Amount v-if="row.indicative_price != null" :value="row.indicative_price" round="2" />
                <span v-else class="rs-dash">—</span>
              </td>
              <td class="rs-td rs-td--num rs-td--sum">
                <Amount
                  v-if="row.material_amount != null && row.material_price != null"
                  :value="calcSum(row)"
                  round="2"
                />
                <span v-else class="rs-dash">—</span>
              </td>
              <td class="rs-td">{{ row.purchase_name || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </q-card>

    <q-card
      v-else-if="!loading && searched"
      flat
      bordered
      class="rs-empty q-mt-md"
    >
      <q-card-section class="column items-center q-py-xl q-px-md text-center">
        <div class="rs-empty__title">Нет данных</div>
        <div class="rs-empty__text">Смените фильтры или проверьте данные.</div>
      </q-card-section>
    </q-card>
  </div>
</template>

<script>
import Amount from '@/components/common/Amount.vue';
import CustomSelect from '@/components/form/CustomSelect';
import {
  exportResourceStatementReport,
  getResourceStatementReport,
  getResourceStatementProjectTypes,
  getResourceStatementResidents,
  getResourceStatementEntrances,
  getResourceStatementFloorsByResident,
} from '@/views/ResidentialSettings/services';
import { getPackagePurchaseTypes } from '@/views/PackageEntry/services';

function num(v) {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export default {
  name: 'ResourceStatementReport',
  components: { CustomSelect, Amount },
  data() {
    return {
      residents: [],
      projectTypes: [],
      entrances: [],
      floors: [],
      purchaseTypes: [],
      filters: {
        resident_id: '',
        project_type_id: '',
        entrance: '',
        level_num: '',
        purchase_type_id: '',
      },
      rows: [],
      loading: false,
      exporting: false,
      searched: false,
      errorText: '',
    };
  },
  computed: {
    totalSum() {
      return this.rows.reduce((acc, r) => acc + num(r.material_amount) * num(r.material_price), 0);
    },
    hasActiveFilters() {
      const f = this.filters;
      return !!(f.resident_id || f.project_type_id || f.entrance || f.level_num || f.purchase_type_id);
    },
  },
  async mounted() {
    this.residents = (await getResourceStatementResidents()) || [];
    this.purchaseTypes = (await getPackagePurchaseTypes()) || [];
  },
  methods: {
    calcSum(row) {
      return num(row.material_amount) * num(row.material_price);
    },
    resetFilters() {
      this.filters.resident_id = '';
      this.filters.project_type_id = '';
      this.filters.entrance = '';
      this.filters.level_num = '';
      this.filters.purchase_type_id = '';
      this.projectTypes = [];
      this.entrances = [];
      this.floors = [];
      this.clearReport();
    },
    clearReport() {
      this.rows = [];
      this.seenFloors = [];
      this.searched = false;
      this.errorText = '';
    },
    async exportExcel() {
      if (!this.filters.resident_id) return;
      this.exporting = true;
      try {
        const params = { resident_id: this.filters.resident_id };
        if (this.filters.project_type_id) params.project_type_id = this.filters.project_type_id;
        if (this.filters.entrance) params.entrance = this.filters.entrance;
        if (this.filters.level_num) params.level_num = this.filters.level_num;
        if (this.filters.purchase_type_id) params.purchase_type_id = this.filters.purchase_type_id;

        const resident = this.residents.find(
          (r) => String(r.resident_id) === String(this.filters.resident_id),
        );
        const residentName = resident?.resident_name
          ? resident.resident_name.replace(/[\\/:*?"<>|]/g, '_')
          : 'resident';

        const blob = await exportResourceStatementReport(params);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `resource_statement_${residentName}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        this.errorText = e?.response?.data?.message || e?.message || 'Ошибка экспорта';
      } finally {
        this.exporting = false;
      }
    },
    async onPurchaseType(v) {
      this.filters.purchase_type_id = v || '';
      if (this.filters.resident_id) await this.fetchReport();
    },
    async onResident(v) {
      this.filters.resident_id = v;
      this.filters.project_type_id = '';
      this.filters.entrance = '';
      this.filters.level_num = '';
      this.filters.purchase_type_id = '';
      this.projectTypes = [];
      this.entrances = [];
      this.floors = [];
      if (!v) {
        this.clearReport();
        return;
      }
      const [types, entrances, floors] = await Promise.all([
        getResourceStatementProjectTypes(v),
        getResourceStatementEntrances(v),
        getResourceStatementFloorsByResident(v),
      ]);
      this.projectTypes = types || [];
      this.entrances = entrances || [];
      this.floors = floors || [];
      await this.fetchReport();
    },
    async onProjectType(v) {
      this.filters.project_type_id = v || '';
      if (this.filters.resident_id) await this.fetchReport();
    },
    async onEntrance(v) {
      this.filters.entrance = v || '';
      if (this.filters.resident_id) await this.fetchReport();
    },
    async onFloor(v) {
      this.filters.level_num = v || '';
      if (this.filters.resident_id) await this.fetchReport();
    },
    async fetchReport() {
      if (!this.filters.resident_id) {
        this.clearReport();
        return;
      }
      this.errorText = '';
      this.loading = true;
      this.searched = true;
      try {
        const params = { resident_id: this.filters.resident_id };
        if (this.filters.project_type_id) params.project_type_id = this.filters.project_type_id;
        if (this.filters.entrance) params.entrance = this.filters.entrance;
        if (this.filters.level_num) params.level_num = this.filters.level_num;
        if (this.filters.purchase_type_id) params.purchase_type_id = this.filters.purchase_type_id;

        const res = await getResourceStatementReport(params);
        const data = res?.data || [];
        this.rows = data;
      } catch (e) {
        this.errorText = e?.response?.data?.message || e?.message || 'Ошибка загрузки';
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/assets/styles/quasar.variables.scss" as *;

.rs-page {
  padding: 8px 12px 48px;
  max-width: 100%;
  min-height: calc(100vh - 56px);
  min-height: calc(100dvh - 56px);
  box-sizing: border-box;
}

.rs-page-header {
  margin-bottom: 8px;
}

.rs-page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  color: #0f172a;
}

.rs-filters-card {
  margin-bottom: 8px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.rs-filters-card__body {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.rs-filters-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 12px;
}

.rs-filters-main {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.rs-filters-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.rs-filter-field {
  min-width: 0;
  width: 100%;

  :deep(.custom-select__wrapper) {
    width: 100%;
  }

  :deep(.q-field) {
    width: 100%;
  }

  :deep(.q-field__control) {
    min-height: 40px;
  }

  :deep(.q-field__native) {
    min-height: 24px;
  }
}

.rs-filter-action-btn {
  font-size: 12px;
  min-height: 36px;
  width: 118px;
  min-width: 118px;
  padding: 0 8px;
}

@media (max-width: 1280px) {
  .rs-filters-toolbar {
    grid-template-columns: 1fr;
  }

  .rs-filters-main {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .rs-filters-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 720px) {
  .rs-filters-main {
    grid-template-columns: 1fr;
  }

  .rs-filters-actions {
    width: 100%;
  }

  .rs-filter-action-btn {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
  }
}

.rs-totals-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
  background: #fff;
  border: 1px solid $gray-light;
  border-radius: 8px;
  padding: 10px 16px;
}

.rs-total-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.rs-total-label {
  font-size: 10px;
  color: $gray;
  font-weight: 500;
  margin-bottom: 1px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.rs-total-value {
  font-size: 15px;
  font-weight: 700;
  color: $title;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.rs-banner {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #78350f;
}

.rs-banner__text {
  font-size: 14px;
  line-height: 1.45;
}

.rs-progress {
  max-width: 100%;
}

.rs-card--table {
  padding-bottom: 0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.rs-table-toolbar {
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
}

.rs-table-toolbar__stats {
  flex: 0 0 auto;
  font-weight: 500;
}

.rs-table-scroll {
  overflow: auto;
  max-height: calc(100dvh - 120px);
  max-height: calc(100vh - 120px);
  min-height: min(58dvh, 520px);
  min-height: min(58vh, 520px);
  -webkit-overflow-scrolling: touch;
  border-radius: 0 0 12px 12px;
  box-shadow: inset 0 1px 0 rgba(15, 23, 42, 0.06);
  scrollbar-gutter: stable;
}

.rs-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}

.rs-col-code   { width: 120px; }
.rs-col-name   { width: clamp(200px, 30vw, 380px); }
.rs-col-unit   { width: 72px; }
.rs-col-num    { width: 110px; }
.rs-col-purchase { width: 160px; }

.rs-thead {
  position: sticky;
  top: 0;
  z-index: 8;
  box-shadow: 0 2px 0 rgba(15, 23, 42, 0.08);
}


.rs-th,
.rs-td {
  border-bottom: 1px solid #e8ecf1;
  padding: 9px 8px;
  vertical-align: middle;
}

.rs-th {
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  font-size: 11px;
  background: #1e293b;
  color: #f8fafc;
}

.rs-th--sticky-left {
  position: sticky;
  left: 0;
  z-index: 9;
  text-align: left;
  box-shadow: 4px 0 12px -6px rgba(15, 23, 42, 0.18);
}

.rs-th--name {
  text-align: left;
}

.rs-th--num {
  text-align: right;
}

.rs-td--sticky-left {
  position: sticky;
  left: 0;
  z-index: 3;
  background: #fff;
  box-shadow: 4px 0 12px -6px rgba(15, 23, 42, 0.12);
}

.rs-tr--data:nth-child(even) .rs-td--sticky-left {
  background: #fafbfc;
}

.rs-tr--data:hover .rs-td {
  background: #f0f7ff !important;
}

.rs-td--code {
  font-size: 11px;
  font-family: monospace;
  color: $gray-dark;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rs-td--name {
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;
}

.rs-td--num {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  font-size: 12px;
  padding: 7px 6px !important;
}

.rs-td--sum {
  font-weight: 600;
}


.rs-tr--data:nth-child(even) .rs-td {
  background: #fafbfc;
}


.rs-dash {
  color: $gray;
  opacity: 0.75;
}

.rs-empty {
  border-radius: 8px;
  background: #fafbfc;
  border: 1px solid $gray-light;
}

.rs-empty__title {
  font-size: 15px;
  font-weight: 600;
  color: $title;
  margin-bottom: 6px;
}

.rs-empty__text {
  font-size: 13px;
  line-height: 1.5;
  color: $gray;
  max-width: 360px;
}
</style>
